import {useContext} from 'react';

import {useTranslation} from 'react-i18next';

import Diversity3OutlinedIcon from '@mui/icons-material/Diversity3Outlined';
import {Avatar, Box, Typography} from '@mui/material';

import {surveyStatisticBars} from './SurveyStatisticBars.colors';
import {LanguageContext} from '../../../../../../../contexts/LanguageContext/LanguageContext';
import {greenColors, orangeColors, neutral} from '../../../../../../../layouts/theme/designSystem';
import {
  CultureTypeDistribution as CultureTypeDistributionData,
  CultureTypeDistributionItem,
} from '../../../types/SurveyTypes';

interface Props {
  distribution?: CultureTypeDistributionData;
}

export const CultureTypeDistribution = ({distribution}: Props) => {
  const {t} = useTranslation();
  const currentLanguage = useContext(LanguageContext) as 'en' | 'de';

  const groups = [
    {
      key: 'management',
      label: t('companySurveys.stats.managers'),
      items: distribution?.management ?? [],
    },
    {
      key: 'employees',
      label: t('companySurveys.stats.employees'),
      items: distribution?.employees ?? [],
    },
  ];

  const hasDistribution = groups.some((group) => group.items.length > 0);

  if (!hasDistribution) return null;

  const renderCultureTypeRow = (item: CultureTypeDistributionItem, total: number) => {
    const percentage = total > 0 ? Math.round((item.count / total) * 100) : 0;
    const name = item.methodType.name[currentLanguage] ?? item.methodType.name.en;

    return (
      <Box
        key={item.methodType.resourceId}
        sx={{
          alignItems: 'center',
          display: 'grid',
          gap: '10px',
          gridTemplateColumns: '32px minmax(0, 1fr) 42px',
        }}
      >
        <Avatar
          src={item.methodType.imageUrl}
          alt={name}
          sx={{
            backgroundColor: orangeColors[7],
            color: greenColors[700],
            height: 32,
            width: 32,
          }}
        />
        <Box sx={{minWidth: 0}}>
          <Box sx={{alignItems: 'center', display: 'flex', gap: '8px', marginBottom: '4px'}}>
            <Typography
              sx={{
                color: greenColors[700],
                flex: 1,
                fontSize: '13px',
                fontWeight: 500,
                lineHeight: 1.4,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {name}
            </Typography>
            <Typography sx={{color: neutral[500], flexShrink: 0, fontSize: '12px', lineHeight: 1.4}}>
              {item.count}
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: surveyStatisticBars.trackMuted,
              borderRadius: '99px',
              height: '6px',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                backgroundColor: surveyStatisticBars.fill,
                borderRadius: '99px',
                height: '100%',
                width: `${percentage}%`,
              }}
            />
          </Box>
        </Box>
        <Typography
          sx={{
            color: percentage > 0 ? greenColors[700] : neutral[500],
            fontSize: '12px',
            fontWeight: 500,
            lineHeight: 1.4,
            textAlign: 'right',
          }}
        >
          {percentage}%
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={{marginTop: '1.5rem'}}>
      <Box
        sx={{
          alignItems: 'center',
          color: greenColors[700],
          display: 'flex',
          gap: '5px',
          marginBottom: '12px',
        }}
      >
        <Diversity3OutlinedIcon sx={{fontSize: 14}} />
        <Typography
          sx={{
            color: 'inherit',
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.04em',
            lineHeight: 1.4,
            textTransform: 'uppercase',
          }}
        >
          {t('companySurveys.details.cultureTypeDistribution')}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: '1rem',
          gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))'},
        }}
      >
        {groups.map((group) => {
          if (group.items.length === 0) return null;

          const total = group.items.reduce((sum, item) => sum + item.count, 0);

          return (
            <Box
              key={group.key}
              sx={{
                backgroundColor: orangeColors[7],
                borderRadius: '8px',
                padding: '1rem',
              }}
            >
              <Typography
                sx={{
                  color: greenColors[700],
                  fontSize: '11px',
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  lineHeight: 1.4,
                  marginBottom: '12px',
                  textTransform: 'uppercase',
                }}
              >
                {group.label}
              </Typography>
              <Box sx={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                {group.items.map((item) => renderCultureTypeRow(item, total))}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
